export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

export function dedupeAttendeeValues(values: string[]): string[] {
  return Array.from(new Map(values.map((value) => {
    const clean = value.trim();
    return [clean.toLowerCase(), clean] as const;
  }).filter(([key]) => Boolean(key))).values());
}

export function normalizeProfileText(value: string): string {
  return value.trim().replace(/\s+/g, ' ').toUpperCase();
}

export function formatDateWithWeekday(date: string): string {
  const weekday = new Intl.DateTimeFormat('en-MY', { weekday: 'long', timeZone: 'Asia/Kuala_Lumpur' })
    .format(new Date(`${date}T00:00:00+08:00`));
  return `${formatDate(date)} (${weekday})`;
}

export function isMtibEmail(email: string): boolean {
  return normalizeEmail(email).endsWith('@mtib.gov.my');
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(normalizeEmail(email));
}

export function hasAttendanceRosterAccess(viewer: { is_host: boolean; is_expected: boolean; has_checked_in: boolean }): boolean {
  return viewer.is_host || viewer.is_expected || viewer.has_checked_in;
}

export function canPrintAttendance(viewer: { is_host: boolean; is_expected: boolean; has_checked_in: boolean }): boolean {
  return hasAttendanceRosterAccess(viewer);
}

export function generateSecret(bytes = 32): string {
  const array = new Uint8Array(bytes);
  crypto.getRandomValues(array);
  return Array.from(array, (b) => b.toString(16).padStart(2, '0')).join('');
}

export function parseExpectedAttendees(raw: string): string[] {
  return Array.from(new Set(
    raw
      .split(/[\n,]+/)
      .map((v) => v.trim())
      .filter(Boolean),
  ));
}

export function getMalaysiaTodayISO(now = new Date()): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: 'Asia/Kuala_Lumpur', year: 'numeric', month: '2-digit', day: '2-digit',
  }).formatToParts(now);
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return `${values.year}-${values.month}-${values.day}`;
}

export function meetingDayState(meetingDate: string): 'past' | 'today' | 'future' {
  const today = getMalaysiaTodayISO();
  if (meetingDate < today) return 'past';
  if (meetingDate > today) return 'future';
  return 'today';
}

export function isMeetingToday(meetingDate: string): boolean {
  return meetingDayState(meetingDate) === 'today';
}

export function formatDate(date: string): string {
  return new Intl.DateTimeFormat('en-MY', {
    day: '2-digit', month: 'short', year: 'numeric', timeZone: 'Asia/Kuala_Lumpur',
  }).format(new Date(`${date}T00:00:00+08:00`));
}

export function formatTime(time: string): string {
  const [hours, minutes] = time.split(':').map(Number);
  if (!Number.isInteger(hours) || !Number.isInteger(minutes) || hours < 0 || hours > 23 || minutes < 0 || minutes > 59) return time;
  return `${hours % 12 || 12}:${String(minutes).padStart(2, '0')} ${hours >= 12 ? 'PM' : 'AM'}`;
}

export function formatTimeRange(startTime: string, endTime: string): string {
  return `${formatTime(startTime)} – ${formatTime(endTime)}`;
}

export function formatMalaysiaIsoTime(iso: string): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    hour: '2-digit', minute: '2-digit', hourCycle: 'h23', timeZone: 'Asia/Kuala_Lumpur',
  }).formatToParts(new Date(iso));
  const values = Object.fromEntries(parts.map((part) => [part.type, part.value]));
  return formatTime(`${values.hour}:${values.minute}`);
}

export function formatDateTime(iso: string): string {
  return new Intl.DateTimeFormat('en-MY', {
    day: '2-digit', month: 'short', hour: 'numeric', minute: '2-digit',
    timeZone: 'Asia/Kuala_Lumpur',
  }).format(new Date(iso));
}

export function errorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  if (!message) return 'Ralat tidak dijangka berlaku.';

  const friendlyMappings: Array<[string, string]> = [
    ['MEETING_GRACE_PERIOD_EXPIRED', 'Butiran mesyuarat hanya boleh dikemaskini dalam tempoh 7 hari selepas mesyuarat tamat.'],
    ['MEETING_SCHEDULE_LOCKED', 'Tarikh dan waktu mesyuarat tidak boleh diubah selepas mesyuarat tamat.'],
    ['MEETING_CANCELLED', 'Mesyuarat yang dibatalkan tidak boleh dikemaskini.'],
    ['MEETING_PROJECTOR_CLOSED', 'Paparan Projector hanya tersedia semasa mesyuarat aktif.'],
    ['ROOM_ALREADY_BOOKED', 'Bilik ini telah ditempah pada waktu yang dipilih. Sila pilih bilik atau waktu lain.'],
    ['MEETING_CONTEXT_LOCKED_AFTER_ATTENDANCE', 'Tarikh, waktu, lokasi dan Pengerusi tidak boleh diubah selepas kehadiran direkodkan.'],
    ['MEETING_NOT_ACTIVE_OR_NOT_TODAY', 'Check-in ini hanya tersedia semasa mesyuarat berlangsung.'],
    ['MEETING_NOT_ACTIVE', 'Mesyuarat tidak aktif untuk tindakan ini.'],
    ['MEETING_DATE_IN_PAST', 'Tarikh mesyuarat tidak boleh dipindahkan ke tarikh yang telah berlalu.'],
    ['SERIES_DATE_PREVIOUSLY_CANCELLED', 'Tarikh siri yang dibatalkan tidak boleh diaktifkan semula. Pilih julat tarikh lain.'],
    ['REPLACEMENT_DEPARTMENT_REQUIRED', 'Terdapat pegawai yang ditugaskan ke bahagian ini. Pilih bahagian gantian dan cuba lagi.'],
    ['REPLACEMENT_DEPARTMENT_NOT_FOUND', 'Bahagian gantian tidak lagi tersedia. Pilih bahagian lain.'],
    ['REPLACEMENT_DEPARTMENT_MUST_DIFFER', 'Pilih bahagian gantian yang berbeza daripada bahagian yang dipadam.'],
    ['USER_ALREADY_EXISTS', 'Pengguna dengan e-mel ini telah wujud. Sunting profil sedia ada.'],
    ['PROFILE_REQUIRED', 'Profil pegawai anda tidak ditemui. Sila log masuk semula.'],
    ['CANNOT_DELETE_CURRENT_ADMIN', 'Akaun Admin yang sedang digunakan tidak boleh dipadam.'],
    ['CANNOT_DEMOTE_CURRENT_ADMIN', 'Peranan Admin sendiri tidak boleh dibuang semasa log masuk.'],
    ['ADMIN_EMAIL_IMMUTABLE', 'E-mel Admin tidak boleh diubah kerana mesti sepadan dengan akaun pengesahan.'],
    ['Date, time, location and chairman cannot be changed', 'Tarikh, waktu, lokasi dan Pengerusi tidak boleh diubah selepas kehadiran direkodkan.'],
    ['Meeting date cannot be moved into the past', 'Tarikh mesyuarat tidak boleh dipindahkan ke tarikh yang telah berlalu.'],
    ['Cancelled meetings cannot be edited', 'Mesyuarat yang dibatalkan tidak boleh dikemaskini.'],
    ['A user with this email already exists', 'Pengguna dengan e-mel ini telah wujud. Sunting profil sedia ada.'],
    ['Admin email cannot be changed', 'E-mel Admin tidak boleh diubah kerana mesti sepadan dengan akaun pengesahan.'],
    ['You cannot remove your own Admin role', 'Peranan Admin sendiri tidak boleh dibuang semasa log masuk.'],
    ['Another user already uses that email', 'E-mel ini telah digunakan oleh pengguna lain.'],
    ['The replacement department is no longer available', 'Bahagian gantian tidak lagi tersedia. Pilih bahagian lain.'],
    ['A cancelled series day cannot be reactivated', 'Tarikh siri yang dibatalkan tidak boleh diaktifkan semula. Pilih julat tarikh lain.'],
    ['A meeting occurrence cannot be moved into the past', 'Tarikh mesyuarat tidak boleh dipindahkan ke tarikh yang telah berlalu.'],
    ['Room no longer available', 'Bilik ini telah ditempah pada waktu yang dipilih. Sila pilih bilik atau waktu lain.'],
  ];
  const marker = friendlyMappings.find(([code]) => message.includes(code));
  if (marker) return marker[1];
  if (/unauthorized|forbidden/i.test(message)) return 'Anda tidak mempunyai kebenaran untuk melaksanakan tindakan ini.';
  if (/record not found|not found/i.test(message)) return 'Rekod yang diminta tidak ditemui.';
  if (/^Unable to\b|^Failed to fetch\b|^Network request failed\b/i.test(message)) return 'Maklumat tidak dapat dimuatkan. Sila cuba lagi.';
  return message;
}

export function formatRelativeTime(value: string): string {
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return 'Masa tidak diketahui';
  const seconds = Math.max(0, Math.round((Date.now() - timestamp) / 1000));
  if (seconds < 60) return 'Sebentar tadi';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minit lalu`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} jam lalu`;
  return `${Math.floor(hours / 24)} hari lalu`;
}

export function appPathFromReturnTo(value: string | null): string {
  const checkInPath = guestCheckInPathFromReturnTo(value);
  if (checkInPath) return checkInPath;
  if (value === '/dashboard' || value === '/profile' || value === '/meetings/new' || value === '/rooms/calendar' || value === '/admin') return value;
  if (value?.startsWith('/meetings/new?')) {
    const target = new URL(value, 'https://kehadiran.mtib.cc');
    const keys = Array.from(target.searchParams.keys()).sort().join(',');
    const date = target.searchParams.get('date') ?? '';
    const room = target.searchParams.get('room') ?? '';
    const start = target.searchParams.get('start') ?? '';
    if (target.origin === 'https://kehadiran.mtib.cc'
      && target.pathname === '/meetings/new'
      && keys === 'date,room,start'
      && /^\d{4}-\d{2}-\d{2}$/.test(date)
      && /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(room)
      && /^\d{2}:\d{2}$/.test(start)) return value;
  }
  if (value && /^\/meetings\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}(?:\?manual=1)?$/i.test(value)) return value;
  return '/dashboard';
}

export function guestCheckInPathFromReturnTo(value: string | null): string | null {
  return value && /^\/checkin\/[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value)
    ? value
    : null;
}

export function normalizeMeetingJoinCode(value: string): string {
  return value.toUpperCase();
}

export function isValidMeetingJoinCode(value: string): boolean {
  return /^[23456789ABCDEFGHJKMNPQRSTUVWXYZ]{6}$/.test(value);
}
